import { Button, Stack, Typography, styled } from '@mui/material';
import { ChangeEvent, FC, useCallback, useRef } from 'react';
import { AttachFileOutlined } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { closeSnackbar } from 'notistack';

import { AttachmentFile } from 'domain/entities/Attachment';
import { useCaseUploadFile } from 'application/attachments/useCases/useCaseUploadFile';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { IconButton, IconContainer, TrashCanIcon } from 'targets/web/components';
import { componentShadows } from 'targets/web/theme/shadows';
import { ACCEPTED_FILE_EXT } from 'targets/web/modules/jobs/components/AttachmentsTable/constants';
import useSnackbar from 'targets/web/modules/dashboard/hooks/useSnackbar';

interface Props {}

const acceptedFiles = ACCEPTED_FILE_EXT.join(', ');

const Container = styled(Stack)(({ theme }) => ({
  flex: '1 0 0',
  alignSelf: 'stretch',
  padding: theme.spacing(6),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  boxShadow: componentShadows.card,
}));

const NoAttachments: FC = () => {
  const t = useTranslationPrefix('customer_portal.request_service_modal');
  return (
    <Container alignItems="center" justifyContent="center" gap={2}>
      <IconContainer padding={2}>
        <AttachFileOutlined sx={{ fontSize: '24px' }} />
      </IconContainer>

      <Typography variant="body1">{t('no_attachments')}</Typography>
    </Container>
  );
};

const AttachmentItem: FC<{
  attachment: AttachmentFile;
  onDelete: () => void;
}> = ({ attachment, onDelete }) => {
  return (
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{
          paddingInline: 2,
          paddingBlock: 1,
          borderRadius: 1,
          borderColor: 'outline',
          borderStyle: 'solid',
          borderWidth: 1,
          width: '100%',
          cursor: 'default',
        }}
      >
        <Stack
          sx={{
            padding: 1.5,
            borderRadius: 1,
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '32px',
            backgroundColor: 'primary.action.selected',
          }}
        >
          <AttachFileOutlined sx={{ color: 'primary.main', fontSize: '20px' }} />
        </Stack>

        <Stack>
          <Typography variant="labelSmall">{attachment.name}</Typography>
          <Typography variant="caption" color="secondary">
            {attachment.contentType}
          </Typography>
        </Stack>
      </Stack>

      <IconButton variant="outlined" color="error" onClick={onDelete}>
        <TrashCanIcon width="24px" height="24px" />
      </IconButton>
    </Stack>
  );
};

const AttachmentsSection: FC<Props> = () => {
  const t = useTranslationPrefix('customer_portal.request_service_modal');
  const { values, setFieldValue } = useFormikContext<{
    attachments: AttachmentFile[];
  }>();
  const notify = useSnackbar();

  const ref = useRef<HTMLInputElement | null>(null);
  const { uploadFile, isPending } = useCaseUploadFile();

  const handleInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || !event.target.files[0]) {
        return;
      }

      const loadingSnackbarKey = notify(t('snackbar.uploading'), {
        variant: 'secondary',
      });

      const file = event.target.files[0];
      const uploadedFile = await uploadFile({ file }).catch((e) => {
        closeSnackbar(loadingSnackbarKey);
        notify(t('snackbar.error'), {
          variant: 'error',
        });

        throw e;
      });

      setFieldValue('attachments', [...values.attachments, uploadedFile]);

      closeSnackbar(loadingSnackbarKey);
      notify(t('snackbar.success'), {
        variant: 'success',
      });

      event.target.value = '';
    },
    [notify, setFieldValue, values, t, uploadFile],
  );

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5">{t('section_attachments')}</Typography>
        <Button
          startIcon={<AttachFileOutlined />}
          size="small"
          variant="contained"
          disabled={isPending}
          onClick={() => {
            ref.current?.click();
          }}
        >
          {t('btn_add_attachment')}
        </Button>

        <input type="file" accept={acceptedFiles} hidden onChange={handleInputChange} ref={ref} />
      </Stack>

      {values.attachments.length === 0 ? (
        <NoAttachments />
      ) : (
        <>
          {values.attachments.map((attachment) => (
            <AttachmentItem
              key={attachment.uri}
              attachment={attachment}
              onDelete={() => {
                setFieldValue(
                  'attachments',
                  values.attachments.filter((a) => a.uri !== attachment.uri),
                );
              }}
            />
          ))}
        </>
      )}
    </>
  );
};

export default AttachmentsSection;
